import { Button, CompassIcon, EmptyState, PlusIcon } from '@buffer-mono/popcorn'

import { FeedsEmptyStateIllustration } from './FeedsEmptyStateIllustration'
import type { FeedCTA } from '../../constants/constants'
import type { WrappedCreateFeedResponse } from '../../hooks/useCreateFeed'
import { ExploreFeedsDialog } from '../ExploreFeedsDialog/ExploreFeedsDialog'
import { SubscribeToFeedDialog } from '../SubscribeToFeedDialog/SubscribeToFeedDialog'

import styles from './EmptyFeedList.module.css'

import type { JSX } from 'react'

type EmptyFeedListProps = {
  existingFeeds: { url: string; id: string }[]
  tier: 'free' | 'paid'
  limitReached: boolean
  onAddFeed: (args: {
    feedUrl: string
    cta: FeedCTA
  }) => Promise<WrappedCreateFeedResponse>
  onRemoveFeed: (feedId: string) => Promise<void>
  onUpgrade?: (args: { cta: CTA; upgradePathName: string }) => void
}

export const EmptyFeedList = ({
  existingFeeds,
  tier,
  limitReached,
  onAddFeed,
  onRemoveFeed,
  onUpgrade,
}: EmptyFeedListProps): JSX.Element => {
  const handleUpgrade = (): void => {
    onUpgrade?.({
      cta: 'create-feeds-exploreFeeds-upgradeButton-1',
      upgradePathName: 'feeds-explore-upgrade',
    })
  }

  return (
    <div className={styles.container}>
      <EmptyState size="large" variant="primary" className={styles.emptyState}>
        <div className={styles.content}>
          <EmptyState.Heading className={styles.heading}>
            Add your First Feed
          </EmptyState.Heading>
          <EmptyState.Description className={styles.description}>
            Subscribe to your favorite blogs, websites, or creators to get the
            latest content ideas.
          </EmptyState.Description>
          <EmptyState.Actions className={styles.actions}>
            <SubscribeToFeedDialog
              existingFeeds={existingFeeds}
              onAddFeed={onAddFeed}
            >
              <Button variant="primary" size="medium">
                <PlusIcon /> New Feed
              </Button>
            </SubscribeToFeedDialog>
            <ExploreFeedsDialog
              existingFeeds={existingFeeds}
              tier={tier}
              type="feeds"
              limitReached={limitReached}
              onAddFeed={onAddFeed}
              onRemoveFeed={onRemoveFeed}
              onUpgrade={handleUpgrade}
            >
              <Button variant="secondary" size="medium">
                <CompassIcon /> Explore Curated Feeds
              </Button>
            </ExploreFeedsDialog>
          </EmptyState.Actions>
        </div>
      </EmptyState>
      <div className={styles.illustration}>
        <FeedsEmptyStateIllustration />
      </div>
    </div>
  )
}
